import { Authenticator, Button, useAuthenticator, View } from '@aws-amplify/ui-react';
import { useMediaQuery } from '@mui/material';
import { ETFPageContainer } from 'components/layout';
import { useMemo } from 'react';
import BackgroundImage from '../../../assets/images/login_background_image.png';
import { AccessibilityLogo } from '../../../components/Logo/AccessibilityLogo';
import './Login.scss';

const formFields = {
    signIn: {
        username: {
            placeholder: 'Email',
            isRequired: true,
        },
        password: {
            placeholder: 'Password',
            isRequired: true,
        },
    },
    resetPassword: {
        username: {
            placeholder: 'Enter your email',
        },
    },
};

const headerDescriptions: Record<string, string> = {
    default: 'Sign in to access the CFRA Accessibility portal.',
    resetPassword:
        "Enter your email to receive a code for resetting your password. Check your junk or spam folder if you don't see it in your inbox.",
    confirmResetPassword: 'Please enter the code emailed to you to complete the password reset process.',
};

export function Login({ rememberMe }: { rememberMe?: React.MutableRefObject<boolean | undefined> }) {
    const { route } = useAuthenticator((context) => [context.route]);
    const isMobile = useMediaQuery('(max-width:999.5px)');

    const AuthHeaderSection: JSX.Element = useMemo(() => {
        const description = headerDescriptions[route] || headerDescriptions.default;
        return <AccessibilityLogo description={description} />;
    }, [route]);

    const components = useMemo(
        () => ({
            Header() {
                return AuthHeaderSection;
            },
            SignIn: {
                Footer() {
                    const { toResetPassword } = useAuthenticator();
                    return (
                        <View
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                maxWidth: '400px',
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <label className='checkbox-label'>
                                    <input
                                        type='checkbox'
                                        onChange={(e) => {
                                            if (!rememberMe) {
                                                return;
                                            }
                                            rememberMe.current = (e.target as HTMLInputElement)?.checked;
                                        }}
                                        className='checkbox-input'
                                        defaultChecked={rememberMe?.current ?? false}
                                    />
                                    <span className={`checkbox-custom ${rememberMe?.current ? 'checked' : ''}`} />
                                    <span style={{ marginLeft: '0.5rem', color: isMobile ? '#fff' : '#555555' }}>
                                        Remember Me
                                    </span>
                                </label>
                            </div>
                            <Button
                                fontWeight='normal'
                                onClick={() => {
                                    toResetPassword();
                                    if (rememberMe === undefined) {
                                        return;
                                    }
                                    rememberMe.current = false;
                                }}
                                size='small'
                                variation='link'
                                className='forgot-password-link'>
                                Forgot Password?
                            </Button>
                        </View>
                    );
                },
            },
        }),
        [AuthHeaderSection, isMobile, rememberMe],
    );

    return (
        <ETFPageContainer
            containerStyles={{
                maxWidth: 'unset',
                backgroundColor: '#fff',
                minHeight: '500px',
            }}>
            <div style={{ display: 'flex', minHeight: '100vh', height: '100%', overflow: 'hidden' }}>
                <div
                    style={{
                        flex: isMobile ? '0 0 100%' : '0 0 50%',
                        backgroundImage: `url(${BackgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div style={{ position: 'absolute', left: '50%', right: isMobile ? '50%' : '0%', border: '50%' }}>
                    <Authenticator formFields={formFields} components={components} hideSignUp={true} />
                </div>
            </div>
        </ETFPageContainer>
    );
}
